import { useCallback, useMemo, useState } from 'react';

import { buildDraft, diffDraft, setLevel, summarizeDraft, toGrants, toggleNode } from '../utils/permissionTree';

/**
 * Edit state of a permission tree: the draft, its Pinned / Modified tags, the counts and the
 * grants to save. Built on the pure functions of `utils/permissionTree.js`.
 *
 * @param {object[]} nodes Permission tree.
 * @param {object[]} grants API grants the draft starts from (and `reset` returns to).
 */
export const usePermissionDraft = (nodes, grants) => {
  const base = useMemo(() => buildDraft(nodes, grants), [nodes, grants]);
  const [draft, setDraft] = useState(base);

  const toggle = useCallback((id, on) => setDraft((d) => toggleNode(nodes, d, id, on)), [nodes]);
  const changeLevel = useCallback((id, level) => setDraft((d) => setLevel(nodes, d, id, level)), [nodes]);
  const reset = useCallback(() => setDraft(base), [base]);

  const { tags, isDirty } = useMemo(() => diffDraft(base, draft), [base, draft]);
  const summary = useMemo(() => summarizeDraft(draft), [draft]);
  const grantsToSave = useMemo(() => toGrants(draft), [draft]);

  return useMemo(
    () => ({ draft, tags, isDirty, summary, grants: grantsToSave, toggle, changeLevel, reset }),
    [draft, tags, isDirty, summary, grantsToSave, toggle, changeLevel, reset],
  );
};
